/**
 * Centralized DateTime Utilities for BT4SAP Frontend
 * 
 * This module provides a single source of truth for all date and time operations
 * in the frontend, ensuring consistency, timezone awareness, and proper formatting.
 * 
 * Core Principles:
 * 1. Auto-detect user timezone from browser
 * 2. Convert UTC timestamps from backend to user's local timezone for display
 * 3. Convert user input to UTC before sending to backend
 * 4. Cache conversions for performance
 * 
 * @module datetimeUtils
 */

import { getCache } from './datetimeCache';
import { trackConversion, trackFormat, trackParse, trackTimezoneDetection, trackCacheHit, trackCacheMiss, trackError } from './datetimeTracking';

/**
 * DateTimeUtils - Centralized date and time utilities
 */
export class DateTimeUtils {
  
  /**
   * Get current timestamp in UTC (ISO8601 string)
   * 
   * @returns {string} ISO8601 formatted UTC timestamp
   * 
   * @example
   * const now = DateTimeUtils.now();
   * // Returns: '2025-01-15T14:30:00.000Z'
   */
  static now() {
    return new Date().toISOString();
  }
  
  /**
   * Parse a date string into a Date object
   * 
   * Supports multiple formats including ISO8601, legacy formats, and timestamps
   * 
   * @param {string|Date|number} dateValue - Date value to parse
   * @param {string} [userTimezone] - User's timezone (for local date strings)
   * @returns {Date|null} Parsed Date object or null if invalid
   * 
   * @example
   * const dt = DateTimeUtils.parse('2025-01-15T14:30:00Z');
   * const dt2 = DateTimeUtils.parse('2025-01-15 14:30:00', 'America/Mexico_City');
   */
  static parse(dateValue, userTimezone = null) {
    const startTime = performance.now();
    let success = true;
    let error = null;
    
    try {
      if (!dateValue) {
        const duration = performance.now() - startTime;
        trackParse('parse_empty', duration, false, null);
        return null;
      }

      // Already a Date object
      if (dateValue instanceof Date) {
        const result = isNaN(dateValue.getTime()) ? null : dateValue;
        const duration = performance.now() - startTime;
        trackParse('parse_date_object', duration, result !== null, error);
        return result;
      }
      
      // Unix timestamp (seconds or milliseconds)
      if (typeof dateValue === 'number') {
        const timestamp = dateValue < 10000000000 ? dateValue * 1000 : dateValue;
        const date = new Date(timestamp);
        const result = isNaN(date.getTime()) ? null : date;
        const duration = performance.now() - startTime;
        trackParse('parse_number', duration, result !== null, null);
        return result;
      }
      
      // String parsing
      if (typeof dateValue === 'string') {
        const cleanDate = dateValue.trim();
        
        // Handle empty strings
        if (cleanDate === '' || cleanDate === 'null' || cleanDate === 'undefined') {
          const duration = performance.now() - startTime;
          trackParse('parse_empty_string', duration, false, null);
          return null;
        }
        
        // Handle ISO8601 format (most common)
        if (cleanDate.includes('T') || cleanDate.match(/^\d{4}-\d{2}-\d{2}/)) {
          try {
            // Try parsing as ISO string
            let date = new Date(cleanDate);
            
            // If parsing failed and no timezone info, try adding UTC
            if (isNaN(date.getTime()) && !cleanDate.includes('Z') && 
                !cleanDate.includes('+') && !cleanDate.includes('-', 10)) {
              date = new Date(cleanDate + 'Z');
            }
            
            if (!isNaN(date.getTime())) {
              const duration = performance.now() - startTime;
              trackParse('parse_iso8601', duration, true, null);
              return date;
            }
          } catch (e) {
            // Continue to other formats
          }
        }
        
        // Handle formatted date strings (MM/DD/YYYY, HH:MM:SS AM/PM TZ)
        // This format comes from toLocaleString() output
        const formattedDatePattern = /^(\d{1,2})\/(\d{1,2})\/(\d{4}),\s*(\d{1,2}):(\d{1,2}):(\d{1,2})\s*(AM|PM)\s+([A-Z]{2,4})$/i;
        const formattedMatch = cleanDate.match(formattedDatePattern);
        if (formattedMatch) {
          try {
            // Try parsing as-is (JavaScript Date can handle this format)
            const date = new Date(cleanDate);
            if (!isNaN(date.getTime())) {
              const duration = performance.now() - startTime;
              trackParse('parse_formatted_locale', duration, true, null);
              return date;
            }
          } catch (e) {
            // Continue to other formats
          }
        }
        
        // Handle legacy formats (DD/MM/YYYY, MM/DD/YYYY)
        const legacyFormats = [
          /^(\d{1,2})\/(\d{1,2})\/(\d{4})(?:\s+(\d{1,2}):(\d{1,2})(?::(\d{1,2}))?)?$/,
          /^(\d{4})-(\d{2})-(\d{2})(?:\s+(\d{1,2}):(\d{1,2})(?::(\d{1,2}))?)?$/
        ];
        
        for (const pattern of legacyFormats) {
          const match = cleanDate.match(pattern);
          if (match) {
            try {
              // Try parsing as local date
              const date = new Date(cleanDate);
              if (!isNaN(date.getTime())) {
                const duration = performance.now() - startTime;
                trackParse('parse_legacy', duration, true, null);
                return date;
              }
            } catch (e) {
              // Continue
            }
          }
        }
        
        // Last resort: try Date constructor
        try {
          const date = new Date(cleanDate);
          if (!isNaN(date.getTime())) {
            const duration = performance.now() - startTime;
            trackParse('parse_fallback', duration, true, null);
            return date;
          }
        } catch (e) {
          // Return null if all parsing attempts fail
          error = e;
          success = false;
        }
      }
      
      // If we get here, parsing failed
      const duration = performance.now() - startTime;
      trackParse('parse_failed', duration, false, error);
      return null;
    } catch (e) {
      error = e;
      success = false;
      const duration = performance.now() - startTime;
      trackParse('parse_error', duration, false, error);
      return null;
    }
  }
  
  /**
   * Format date with preset or custom format
   * 
   * @param {string|Date|number} dateValue - Date value to format
   * @param {string} [formatPreset='long'] - Format preset ('short', 'long', 'full', 'date_only', 'time_only', 'relative')
   * @param {Object} [options={}] - Intl.DateTimeFormat options
   * @returns {string} Formatted date string or 'N/A' if invalid
   * 
   * @example
   * DateTimeUtils.format(date, 'short');
   * DateTimeUtils.format(date, 'long', { timeZone: 'America/Mexico_City' });
   */
  static format(dateValue, formatPreset = 'long', options = {}) {
    const startTime = performance.now();
    let success = true;
    let error = null;
    
    try {
      const date = this.parse(dateValue);
      if (!date) {
        trackFormat('format_invalid', performance.now() - startTime, false, new Error('Invalid date'));
        return 'N/A';
      }
      
      const presets = {
        short: {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit'
        },
        long: {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit'
        },
        full: {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
          timeZoneName: 'short'
        },
        date_only: {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit'
        },
        time_only: {
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit'
        },
        relative: null // Special handling for relative format
      };
      
      // Handle relative format
      if (formatPreset === 'relative') {
        return this.formatRelative(date);
      }
      
      // Get preset options
      const presetOptions = presets[formatPreset] || presets.long;
      
      // Merge with custom options
      const formatOptions = { ...presetOptions, ...options };
      
      try {
        const result = new Intl.DateTimeFormat('en-US', formatOptions).format(date);
        const duration = performance.now() - startTime;
        trackFormat('format', duration, true, null);
        return result;
      } catch (e) {
        error = e;
        success = false;
        console.warn('Error formatting date:', e);
        const result = date.toLocaleString();
        const duration = performance.now() - startTime;
        trackFormat('format_fallback', duration, false, error);
        return result;
      }
    } catch (e) {
      error = e;
      success = false;
      const duration = performance.now() - startTime;
      trackFormat('format_error', duration, false, error);
      return 'N/A';
    }
  }
  
  /**
   * Format UTC timestamp for display in user's local timezone (cached)
   * 
   * @param {string|Date|number} utcTimestamp - UTC timestamp from backend
   * @param {Object} [options={}] - Formatting options
   * @returns {string} Formatted date string in user's timezone
   * 
   * @example
   * const formatted = DateTimeUtils.formatForUser('2025-01-15T14:30:00Z');
   * // Returns date formatted in user's browser timezone
   */
  static formatForUser(utcTimestamp, options = {}) {
    const startTime = performance.now();
    let success = true;
    let error = null;
    
    try {
      if (!utcTimestamp) {
        trackFormat('formatForUser_empty', performance.now() - startTime, false, new Error('Empty timestamp'));
        return 'N/A';
      }

      const date = this.parse(utcTimestamp);
      if (!date) {
        trackFormat('formatForUser_invalid', performance.now() - startTime, false, new Error('Invalid date'));
        return 'N/A';
      }

      const userTimezone = this.getUserTimezone();
      const formatPreset = options.formatPreset || 'long';
      
      // Check cache
      const cache = getCache();
      const cacheKey = date.getTime();
      const cached = cache.getFormat(cacheKey, formatPreset, userTimezone);
      if (cached !== null) {
        trackCacheHit('format');
        const duration = performance.now() - startTime;
        trackFormat('formatForUser_cached', duration, true, null);
        return cached;
      }
      trackCacheMiss('format');
      
      const defaultOptions = {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        timeZoneName: 'short',
        timeZone: userTimezone
      };
      
      const formatOptions = { ...defaultOptions, ...options };
      
      try {
        const formatted = new Intl.DateTimeFormat('en-US', formatOptions).format(date);
        // Cache result
        cache.setFormat(cacheKey, formatPreset, userTimezone, formatted);
        const duration = performance.now() - startTime;
        trackFormat('formatForUser', duration, true, null);
        return formatted;
      } catch (e) {
        error = e;
        success = false;
        console.warn('Error formatting date for user:', e);
        const result = date.toLocaleString();
        const duration = performance.now() - startTime;
        trackFormat('formatForUser_error', duration, false, error);
        return result;
      }
    } catch (e) {
      error = e;
      success = false;
      const duration = performance.now() - startTime;
      trackFormat('formatForUser_exception', duration, false, error);
      return 'N/A';
    }
  }
  
  /**
   * Convert local date/time to UTC ISO string for backend
   * 
   * @param {string|Date|number} localDateTime - Local date/time value
   * @returns {string} ISO8601 UTC string
   * 
   * @example
   * const utcString = DateTimeUtils.toUTC('2025-01-15 14:30:00');
   * // Returns: '2025-01-15T14:30:00.000Z'
   */
  static toUTC(localDateTime) {
    const startTime = performance.now();
    let success = true;
    let error = null;
    
    try {
      const date = this.parse(localDateTime);
      if (!date) {
        trackConversion('toUTC_invalid', performance.now() - startTime, false, new Error('Invalid date'));
        return null;
      }

      const result = date.toISOString();
      const duration = performance.now() - startTime;
      trackConversion('toUTC', duration, true, null);
      return result;
    } catch (e) {
      error = e;
      success = false;
      const duration = performance.now() - startTime;
      trackConversion('toUTC_error', duration, false, error);
      return null;
    }
  }
  
  /**
   * Get user's timezone from browser (cached)
   * 
   * @returns {string} IANA timezone identifier (e.g., 'America/Mexico_City')
   * 
   * @example
   * const tz = DateTimeUtils.getUserTimezone();
   * // Returns: 'America/Mexico_City' or detected timezone
   */
  static getUserTimezone() {
    const cache = getCache();
    const cached = cache.getTimezone();
    
    // Track based on whether it was cached or newly detected
    if (cache.timezoneCache && cache.timezoneCacheTimestamp) {
      // Was already cached (getTimezone increments stats internally)
      trackCacheHit('timezone');
      trackTimezoneDetection(cached, 'cached');
    } else {
      // Was just detected
      trackCacheMiss('timezone');
      trackTimezoneDetection(cached, 'detected');
    }
    
    return cached || 'UTC';
  }
  
  /**
   * Get user's timezone information
   * 
   * @returns {Object} Timezone info object with timezone, offset, etc.
   */
  static getUserTimezoneInfo() {
    const timezone = this.getUserTimezone();
    const now = new Date();
    
    try {
      const formatter = new Intl.DateTimeFormat('en', {
        timeZone: timezone,
        timeZoneName: 'short'
      });
      
      const parts = formatter.formatToParts(now);
      const timeZoneName = parts.find(part => part.type === 'timeZoneName')?.value || 'Unknown';
      
      return {
        timezone,
        timeZoneName,
        offset: -now.getTimezoneOffset(), // Offset in minutes from UTC
        offsetHours: -now.getTimezoneOffset() / 60,
        sample: now.toLocaleString('en-US', { timeZone: timezone })
      };
    } catch (error) {
      console.warn('Error getting timezone info:', error);
      return {
        timezone: 'UTC',
        timeZoneName: 'UTC',
        offset: 0,
        offsetHours: 0,
        sample: now.toISOString()
      };
    }
  }
  
  /**
   * Add timezone headers to request headers
   * 
   * @param {Object} [headers={}] - Existing headers object
   * @returns {Object} Headers object with timezone headers added
   * 
   * @example
   * const headers = DateTimeUtils.addTimezoneHeaders({ 'Content-Type': 'application/json' });
   * // Adds: 'X-User-Timezone': 'America/Mexico_City'
   */
  static addTimezoneHeaders(headers = {}) {
    const userTimezone = this.getUserTimezone();
    
    return {
      ...headers,
      'X-User-Timezone': userTimezone,
      'X-User-Locale': navigator.language || 'en-US'
    };
  }
  
  /**
   * Create options for apiCall with timezone headers
   * 
   * @param {Object} [options={}] - Existing options object
   * @returns {Object} Options object with timezone headers added
   */
  static createApiCallOptions(options = {}) {
    const currentHeaders = options.headers || {};
    
    return {
      ...options,
      headers: this.addTimezoneHeaders(currentHeaders)
    };
  }
  
  /**
   * Format date as relative time (e.g., "2 hours ago", "yesterday")
   * 
   * @param {string|Date|number} dateValue - Date value to format
   * @returns {string} Relative time string
   */
  static formatRelative(dateValue) {
    const date = this.parse(dateValue);
    if (!date) return 'N/A';
    
    const now = new Date();
    const diffMs = now - date;
    const diffSecs = Math.floor(diffMs / 1000);
    const diffMins = Math.floor(diffSecs / 60);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);
    
    if (diffSecs < 60) {
      return 'Just now';
    } else if (diffMins < 60) {
      return `${diffMins} minute${diffMins !== 1 ? 's' : ''} ago`;
    } else if (diffHours < 24) {
      return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
    } else if (diffDays === 1) {
      return 'Yesterday';
    } else if (diffDays < 7) {
      return `${diffDays} days ago`;
    } else if (diffDays < 30) {
      const weeks = Math.floor(diffDays / 7);
      return `${weeks} week${weeks !== 1 ? 's' : ''} ago`;
    } else if (diffDays < 365) {
      const months = Math.floor(diffDays / 30);
      return `${months} month${months !== 1 ? 's' : ''} ago`;
    } else {
      const years = Math.floor(diffDays / 365);
      return `${years} year${years !== 1 ? 's' : ''} ago`;
    }
  }
  
  /**
   * Check if two dates are on the same day (in user's timezone)
   * 
   * @param {string|Date|number} date1 - First date
   * @param {string|Date|number} date2 - Second date
   * @returns {boolean} True if same day
   */
  static isSameDay(date1, date2) {
    const d1 = this.parse(date1);
    const d2 = this.parse(date2);
    
    if (!d1 || !d2) return false;
    
    const userTimezone = this.getUserTimezone();
    
    try {
      const day1 = new Intl.DateTimeFormat('en-CA', {
        timeZone: userTimezone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      }).format(d1);
      
      const day2 = new Intl.DateTimeFormat('en-CA', {
        timeZone: userTimezone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      }).format(d2);
      
      return day1 === day2;
    } catch (error) {
      console.warn('Error comparing dates:', error);
      return false;
    }
  }
  
  /**
   * Check if date is today (in user's timezone)
   * 
   * @param {string|Date|number} dateValue - Date to check
   * @returns {boolean} True if today
   */
  static isToday(dateValue) {
    return this.isSameDay(dateValue, new Date());
  }
  
  /**
   * Check if date is in the past
   * 
   * @param {string|Date|number} dateValue - Date to check
   * @returns {boolean} True if in the past
   */
  static isPast(dateValue) {
    const date = this.parse(dateValue);
    if (!date) return false;
    
    return date < new Date();
  }
  
  /**
   * Check if date is in the future
   * 
   * @param {string|Date|number} dateValue - Date to check
   * @returns {boolean} True if in the future
   */
  static isFuture(dateValue) {
    const date = this.parse(dateValue);
    if (!date) return false;
    
    return date > new Date();
  }
  
  /**
   * Get start of day in UTC for user's current day
   * 
   * @returns {string} ISO8601 UTC string for start of user's current day
   */
  static getStartOfDayUTC() {
    try {
      const userTimezone = this.getUserTimezone();
      const now = new Date();
      
      // Get the date string in user's timezone
      const localDateString = new Intl.DateTimeFormat('en-CA', {
        timeZone: userTimezone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      }).format(now);
      
      // Create start of day in user's timezone
      const startOfDay = new Date(`${localDateString}T00:00:00`);
      
      // Adjust for timezone offset to get UTC
      const offsetMinutes = startOfDay.getTimezoneOffset();
      const startOfDayUTC = new Date(startOfDay.getTime() + (offsetMinutes * 60000));
      
      return startOfDayUTC.toISOString();
    } catch (error) {
      console.warn('Error getting start of day UTC:', error);
      const now = new Date();
      now.setHours(0, 0, 0, 0);
      return now.toISOString();
    }
  }

  /**
   * Format a date value as an ISO date (YYYY-MM-DD) in a specific timezone.
   * Useful for API date-only parameters and avoids timezone off-by-one issues.
   *
   * @param {string|Date|number} dateValue - Date value to format
   * @param {Object} [options={}]
   * @param {string} [options.timeZone] - IANA timezone identifier (defaults to user's timezone)
   * @returns {string|null} ISO date string (YYYY-MM-DD) or null if invalid
   */
  static formatISODate(dateValue, options = {}) {
    try {
      const date = this.parse(dateValue);
      if (!date) return null;

      const tz = options.timeZone || this.getUserTimezone();
      // en-CA reliably formats as YYYY-MM-DD
      return new Intl.DateTimeFormat('en-CA', {
        timeZone: tz,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      }).format(date);
    } catch (e) {
      // Fallback to UTC YYYY-MM-DD if Intl fails
      try {
        const date = this.parse(dateValue);
        return date ? date.toISOString().slice(0, 10) : null;
      } catch {
        return null;
      }
    }
  }

  /**
   * Format date with custom format string (e.g., 'MM/DD/YYYY', 'YYYY-MM-DD', 'MM/YYYY')
   * 
   * Supports common format patterns:
   * - MM/DD/YYYY - US format (12/17/2025)
   * - YYYY-MM-DD - ISO date format (2025-12-17)
   * - DD/MM/YYYY - European format (17/12/2025)
   * - MM/YYYY - Month/Year only (12/2025)
   * - YYYY/MM - Year/Month only (2025/12)
   * - Custom patterns using Intl.DateTimeFormat options
   * 
   * @param {string|Date|number} dateValue - Date value to format
   * @param {string} formatString - Format string ('MM/DD/YYYY', 'YYYY-MM-DD', etc.) or preset name
   * @param {Object} [options={}] - Additional Intl.DateTimeFormat options
   * @returns {string} Formatted date string or 'N/A' if invalid
   * 
   * @example
   * // US format
   * DateTimeUtils.formatCustom('2025-12-17T14:30:00Z', 'MM/DD/YYYY')
   * // Returns: "12/17/2025"
   * 
   * // ISO format
   * DateTimeUtils.formatCustom('2025-12-17T14:30:00Z', 'YYYY-MM-DD')
   * // Returns: "2025-12-17"
   * 
   * // Month/Year only
   * DateTimeUtils.formatCustom('2025-12-17T14:30:00Z', 'MM/YYYY')
   * // Returns: "12/2025"
   * 
   * // European format
   * DateTimeUtils.formatCustom('2025-12-17T14:30:00Z', 'DD/MM/YYYY')
   * // Returns: "17/12/2025"
   */
  static formatCustom(dateValue, formatString, options = {}) {
    const startTime = performance.now();
    
    try {
      const date = this.parse(dateValue);
      if (!date) {
        trackFormat('formatCustom_invalid', performance.now() - startTime, false, new Error('Invalid date'));
        return 'N/A';
      }

      const userTimezone = this.getUserTimezone();
      
      // Predefined format patterns
      const formatPatterns = {
        'MM/DD/YYYY': {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit'
        },
        'YYYY-MM-DD': {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit'
        },
        'DD/MM/YYYY': {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit'
        },
        'MM/YYYY': {
          year: 'numeric',
          month: '2-digit'
        },
        'YYYY/MM': {
          year: 'numeric',
          month: '2-digit'
        },
        'MM/DD': {
          month: '2-digit',
          day: '2-digit'
        },
        'DD/MM': {
          month: '2-digit',
          day: '2-digit'
        }
      };

      // Get format options
      let formatOptions = formatPatterns[formatString];
      
      if (!formatOptions) {
        // If not a predefined pattern, try to parse as Intl options
        // For now, default to date_only preset
        formatOptions = {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit'
        };
      }

      // Merge with user options
      formatOptions = { ...formatOptions, ...options, timeZone: userTimezone };

      // Format using Intl.DateTimeFormat
      const formatter = new Intl.DateTimeFormat('en-US', formatOptions);
      let formatted = formatter.format(date);

      // Post-process to match exact format string
      if (formatString === 'MM/DD/YYYY') {
        // Intl returns "MM/DD/YYYY" format by default for en-US
        // Already correct, but ensure consistency
        const parts = formatter.formatToParts(date);
        const month = parts.find(p => p.type === 'month')?.value || '';
        const day = parts.find(p => p.type === 'day')?.value || '';
        const year = parts.find(p => p.type === 'year')?.value || '';
        formatted = `${month}/${day}/${year}`;
      } else if (formatString === 'YYYY-MM-DD') {
        // Convert to ISO date format
        const parts = formatter.formatToParts(date);
        const month = parts.find(p => p.type === 'month')?.value || '';
        const day = parts.find(p => p.type === 'day')?.value || '';
        const year = parts.find(p => p.type === 'year')?.value || '';
        formatted = `${year}-${month}-${day}`;
      } else if (formatString === 'DD/MM/YYYY') {
        // European format
        const parts = formatter.formatToParts(date);
        const month = parts.find(p => p.type === 'month')?.value || '';
        const day = parts.find(p => p.type === 'day')?.value || '';
        const year = parts.find(p => p.type === 'year')?.value || '';
        formatted = `${day}/${month}/${year}`;
      } else if (formatString === 'MM/YYYY') {
        // Month/Year format
        const parts = formatter.formatToParts(date);
        const month = parts.find(p => p.type === 'month')?.value || '';
        const year = parts.find(p => p.type === 'year')?.value || '';
        formatted = `${month}/${year}`;
      } else if (formatString === 'YYYY/MM') {
        // Year/Month format
        const parts = formatter.formatToParts(date);
        const month = parts.find(p => p.type === 'month')?.value || '';
        const year = parts.find(p => p.type === 'year')?.value || '';
        formatted = `${year}/${month}`;
      }

      trackFormat('formatCustom', performance.now() - startTime, true, null);
      return formatted;
    } catch (e) {
      trackFormat('formatCustom_error', performance.now() - startTime, false, e);
      console.warn('Error formatting date with custom format:', e);
      return 'N/A';
    }
  }

  /**
   * Parse date from custom format string (e.g., 'MM/DD/YYYY', 'YYYY-MM-DD')
   * 
   * Supports common format patterns and converts to Date object.
   * Always assumes the date is in user's local timezone unless specified.
   * 
   * @param {string} dateString - Date string in custom format
   * @param {string} formatString - Format string ('MM/DD/YYYY', 'YYYY-MM-DD', etc.)
   * @param {Object} [options={}] - Parsing options
   * @returns {Date|null} Parsed Date object or null if invalid
   * 
   * @example
   * // Parse US format
   * DateTimeUtils.parseCustom('12/17/2025', 'MM/DD/YYYY')
   * 
   * // Parse ISO format
   * DateTimeUtils.parseCustom('2025-12-17', 'YYYY-MM-DD')
   * 
   * // Parse Month/Year
   * DateTimeUtils.parseCustom('12/2025', 'MM/YYYY')
   */
  static parseCustom(dateString, formatString, options = {}) {
    const startTime = performance.now();
    
    try {
      if (!dateString || typeof dateString !== 'string') {
        trackParse('parseCustom_invalid', performance.now() - startTime, false, new Error('Invalid input'));
        return null;
      }

      const cleanDate = dateString.trim();
      if (!cleanDate) {
        return null;
      }

      let date = null;

      // Handle different format patterns
      if (formatString === 'MM/DD/YYYY') {
        // US format: MM/DD/YYYY
        const match = cleanDate.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
        if (match) {
          const [, month, day, year] = match;
          // Create date in user's timezone (local midnight)
          date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
        }
      } else if (formatString === 'YYYY-MM-DD') {
        // ISO format: YYYY-MM-DD
        const match = cleanDate.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
        if (match) {
          const [, year, month, day] = match;
          date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
        }
      } else if (formatString === 'DD/MM/YYYY') {
        // European format: DD/MM/YYYY
        const match = cleanDate.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
        if (match) {
          const [, day, month, year] = match;
          date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
        }
      } else if (formatString === 'MM/YYYY') {
        // Month/Year format: MM/YYYY
        const match = cleanDate.match(/^(\d{1,2})\/(\d{4})$/);
        if (match) {
          const [, month, year] = match;
          // Set to first day of month
          date = new Date(parseInt(year), parseInt(month) - 1, 1);
        }
      } else if (formatString === 'YYYY/MM') {
        // Year/Month format: YYYY/MM
        const match = cleanDate.match(/^(\d{4})\/(\d{1,2})$/);
        if (match) {
          const [, year, month] = match;
          date = new Date(parseInt(year), parseInt(month) - 1, 1);
        }
      }

      if (date && !isNaN(date.getTime())) {
        trackParse('parseCustom', performance.now() - startTime, true, null);
        return date;
      }

      // Fallback: try standard parse
      const fallbackDate = this.parse(cleanDate);
      if (fallbackDate) {
        trackParse('parseCustom_fallback', performance.now() - startTime, true, null);
        return fallbackDate;
      }

      trackParse('parseCustom_failed', performance.now() - startTime, false, new Error('Parse failed'));
      return null;
    } catch (e) {
      trackParse('parseCustom_error', performance.now() - startTime, false, e);
      return null;
    }
  }

  /**
   * Convert custom format date to UTC ISO string for backend
   * 
   * Parses a date string in custom format and converts it to UTC ISO string
   * suitable for sending to backend.
   * 
   * @param {string} dateString - Date string in custom format
   * @param {string} formatString - Format string ('MM/DD/YYYY', 'YYYY-MM-DD', etc.)
   * @param {Object} [options={}] - Options (assumeLocal: true = assume local timezone)
   * @returns {string|null} UTC ISO string or null if invalid
   * 
   * @example
   * // Convert US format to UTC ISO
   * DateTimeUtils.customToUTC('12/17/2025', 'MM/DD/YYYY')
   * // Returns: "2025-12-17T00:00:00.000Z" (midnight UTC)
   * 
   * // Convert with time
   * DateTimeUtils.customToUTC('12/17/2025 14:30', 'MM/DD/YYYY HH:mm')
   * // Returns: "2025-12-17T14:30:00.000Z"
   */
  static customToUTC(dateString, formatString, options = {}) {
    try {
      const date = this.parseCustom(dateString, formatString, options);
      if (!date) {
        return null;
      }

      // Convert to UTC ISO string
      return this.toUTC(date);
    } catch (e) {
      console.warn('Error converting custom format to UTC:', e);
      return null;
    }
  }
  
  /**
   * Debug timezone information (for development)
   * 
   * @returns {Object} Timezone debug info
   */
  static debugTimezone() {
    const info = this.getUserTimezoneInfo();
    console.group('🌍 Timezone Debug Info');
    console.log('Timezone:', info.timezone);
    console.log('Timezone Name:', info.timeZoneName);
    console.log('Offset from UTC:', `${info.offsetHours} hours (${info.offset} minutes)`);
    console.log('Sample time:', info.sample);
    console.log('User Agent Locale:', navigator.language);
    console.groupEnd();
    return info;
  }
}

// Convenience functions for easier usage

/**
 * Get current timestamp in UTC
 * @returns {string} ISO8601 UTC timestamp
 */
export const now = () => DateTimeUtils.now();

/**
 * Parse date string
 * @param {string|Date|number} dateValue - Date value to parse
 * @returns {Date|null} Parsed Date object
 */
export const parse = (dateValue) => DateTimeUtils.parse(dateValue);

/**
 * Format date
 * @param {string|Date|number} dateValue - Date value to format
 * @param {string} formatPreset - Format preset
 * @param {Object} options - Formatting options
 * @returns {string} Formatted date string
 */
export const format = (dateValue, formatPreset = 'long', options = {}) => 
  DateTimeUtils.format(dateValue, formatPreset, options);

/**
 * Format UTC timestamp for user display
 * @param {string|Date|number} utcTimestamp - UTC timestamp
 * @param {Object} options - Formatting options
 * @returns {string} Formatted date string
 */
export const formatForUser = (utcTimestamp, options = {}) => 
  DateTimeUtils.formatForUser(utcTimestamp, options);

/**
 * Format date with custom format string (e.g., 'MM/DD/YYYY', 'YYYY-MM-DD')
 * @param {string|Date|number} dateValue - Date value
 * @param {string} formatString - Format string ('MM/DD/YYYY', 'YYYY-MM-DD', etc.)
 * @param {Object} options - Additional options
 * @returns {string} Formatted date string
 */
export const formatCustom = (dateValue, formatString, options = {}) =>
  DateTimeUtils.formatCustom(dateValue, formatString, options);

/**
 * Parse date from custom format string
 * @param {string} dateString - Date string in custom format
 * @param {string} formatString - Format string ('MM/DD/YYYY', 'YYYY-MM-DD', etc.)
 * @param {Object} options - Parsing options
 * @returns {Date|null} Parsed Date object or null
 */
export const parseCustom = (dateString, formatString, options = {}) =>
  DateTimeUtils.parseCustom(dateString, formatString, options);

/**
 * Convert custom format date to UTC ISO string for backend
 * @param {string} dateString - Date string in custom format
 * @param {string} formatString - Format string ('MM/DD/YYYY', 'YYYY-MM-DD', etc.)
 * @param {Object} options - Options
 * @returns {string|null} UTC ISO string or null
 */
export const customToUTC = (dateString, formatString, options = {}) =>
  DateTimeUtils.customToUTC(dateString, formatString, options);

/**
 * Convert to UTC
 * @param {string|Date|number} localDateTime - Local date/time
 * @returns {string} ISO8601 UTC string
 */
export const toUTC = (localDateTime) => DateTimeUtils.toUTC(localDateTime);

/**
 * Get user timezone
 * @returns {string} IANA timezone identifier
 */
export const getUserTimezone = () => DateTimeUtils.getUserTimezone();

/**
 * Add timezone headers
 * @param {Object} headers - Existing headers
 * @returns {Object} Headers with timezone added
 */
export const addTimezoneHeaders = (headers = {}) => 
  DateTimeUtils.addTimezoneHeaders(headers);

/**
 * Format date as ISO date (YYYY-MM-DD) in user's timezone (or provided timezone).
 * @param {string|Date|number} dateValue - Date value to format
 * @param {Object} [options={}]
 * @returns {string|null}
 */
export const formatISODate = (dateValue, options = {}) =>
  DateTimeUtils.formatISODate(dateValue, options);

/**
 * Wrapper for apiCall with automatic timezone headers
 * @param {string} endpoint - API endpoint
 * @param {string} method - HTTP method
 * @param {*} data - Request data
 * @param {boolean} forceReauth - Force reauthentication
 * @param {Object} options - Additional options
 * @returns {Promise} API call result
 */
export const apiCallWithTimezone = async (endpoint, method = 'GET', data = null, forceReauth = false, options = {}) => {
  // Import apiCall dynamically to avoid circular dependencies
  const { apiCall } = await import('../services/api.service');
  
  // Add timezone headers
  const timezoneOptions = DateTimeUtils.createApiCallOptions(options);
  
  return apiCall(endpoint, method, data, forceReauth, timezoneOptions);
};

// Auto-initialization (optional, for development)
if (process.env.NODE_ENV === 'development') {
  console.log('🌍 DateTime Utils loaded for:', DateTimeUtils.getUserTimezone());
}

export default DateTimeUtils;
